import { z } from 'zod';
/**
 * Regex pattern for validating time format in HH:MM format
 * Allows single digit hours (0-9) without leading zeros and double digit hours (10-23)
 * Requires minutes to be 2 digits (00-59)
 */
export const TIME_REGEX = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
export const FieldType = {
    TEXT: 'TEXT',
    TEXTAREA: 'TEXTAREA',
    SELECT: 'SELECT',
    MULTI_SELECT: 'MULTI_SELECT',
    CHECKBOX: 'CHECKBOX',
    CHECKBOX_GROUP: 'CHECKBOX_GROUP',
    DATE: 'DATE',
    TIME: 'TIME',
    NUMBER: 'NUMBER',
    CURRENCY: 'CURRENCY',
    SUGGESTED_TEXT: 'SUGGESTED_TEXT',
    RICH_TEXT: 'RICH_TEXT',
};
export const FieldStatus = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    ARCHIVED: 'ARCHIVED',
};
export const FieldCategory = {
    LEGISLATIVE_ITEM: 'legislative_item',
    SEAT_APPLICATION: 'seat_application',
};
// Option object schema. `value` is the canonical persisted value for SELECT,
// MULTI_SELECT, and CHECKBOX_GROUP fields. `label` is only used by
// SUGGESTED_TEXT fields, where it acts as the short reference for the longer
// boilerplate `value` inserted on pick. Keeping it optional in a single schema
// (rather than a union) preserves the field for SUGGESTED_TEXT payloads while
// leaving SELECT-family payloads unchanged.
export const optionSchema = z.object({
    value: z.string('Option value is required').min(1, 'Option value is required'),
    label: z
        .string()
        .optional()
        .describe('Only used by SUGGESTED_TEXT types. Display label the user sees in the select dropdown. `value` is what is inserted into the input.'),
});
// Options validation - array of option objects
export const fieldOptionsSchema = z.array(optionSchema).optional();
// Base schema for custom fields
export const baseCustomFieldSchema = z.object({
    name: z.string().min(1, 'Name is required').max(1000, 'Name must be less than 1000 characters'),
    description: z.string().nullish(),
    type: z.enum(FieldType),
    isActive: z.boolean().optional(),
    options: fieldOptionsSchema,
    category: z.enum(FieldCategory, 'Category is required'),
    tagIds: z.array(z.number()).optional(),
});
export const collectCustomFieldOptionIssues = (type, options) => {
    const issues = [];
    if (type === FieldType.SELECT ||
        type === FieldType.MULTI_SELECT ||
        type === FieldType.CHECKBOX_GROUP) {
        if (!options || options.length === 0) {
            issues.push({ path: ['options'], message: 'At least one option is required.' });
        }
    }
    if (type === FieldType.SUGGESTED_TEXT) {
        if (!options || options.length === 0) {
            issues.push({ path: ['options'], message: 'At least one suggestion is required.' });
            return issues;
        }
        options.forEach((option, index) => {
            if (!option.label || option.label.trim() === '') {
                issues.push({
                    path: ['options', index, 'label'],
                    message: 'Suggestion label is required',
                });
            }
        });
    }
    return issues;
};
const applyOptionIssues = (data, ctx) => {
    for (const issue of collectCustomFieldOptionIssues(data.type, data.options)) {
        ctx.addIssue({ code: 'custom', path: issue.path, message: issue.message });
    }
};
// Create validation and DTO type
export const customFieldCreateSchema = baseCustomFieldSchema.superRefine(applyOptionIssues);
// Text-based types can be converted into one another after creation; the stored values are
// migrated to the new format. Every other type is immutable once created.
export const TEXT_CONVERTIBLE_FIELD_TYPES = [
    FieldType.TEXT,
    FieldType.TEXTAREA,
    FieldType.RICH_TEXT,
];
export const isTextConvertibleFieldType = (type) => TEXT_CONVERTIBLE_FIELD_TYPES.includes(type);
// Update validation and DTO type. Type changes and options validation must be checked in the
// controller (which knows the existing type), see `isTextConvertibleFieldType` and
// `collectCustomFieldOptionIssues`.
export const customFieldUpdateSchema = baseCustomFieldSchema.partial();
// Re-export the custom field schema function
export { getFormSchemaWithCustomFieldRequirements, isCustomFieldValueEmpty, } from './custom-field-schema.js';
export { parentConditionCheckboxDisplayLabel, parentConditionDisplayLabel, parentConditionValueSchema, parentValueMatchesParentCondition, } from './parent-condition-value.js';
export { resolveVisibility } from './resolve-visibility.js';
